/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout, TextStyle, Link, Icon, RangeSlider } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import { DropdownIndicator, styles } from './react-select-style';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { normalizeNumberRangeSlider, minValue } from 'src/helpers/form-validation-helper';
import {
  DEFAULT_FONT_SIZE,
  IMAGE_SIZE_OPTIONS,
  TEXT_DESCRIPTION_OPTIONS,
  MIN_FONT_SIZE,
  MAX_FONT_SIZE
} from 'src/constants/theme-settings.js';
import { URL } from 'src/constants/links';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

const validateMinFontSize = minValue(MIN_FONT_SIZE);

class CollectionHeaderProductCountForm extends Component<ComposedProps> {
  state = {
    isOpenedCheckout: true,
    isOpenedViewCart: true
  };

  /**
   * Blur text field
   * @param {*} event
   * @param {*} value
   * @param {*} name
   */
  onBlurTextField = (event, value, name) => {
    const { updateField } = this.props;
    if (!value) {
      event.preventDefault();
      updateField(name, DEFAULT_FONT_SIZE);
    }
  };

  /**
   * Get layout option with translation label
   * @returns
   */
  getImageSizes = () => {
    const { i18n } = this.props;
    return IMAGE_SIZE_OPTIONS.map(option => {
      const { label, value } = option;

      return { label: i18n.translate(label), value };
    });
  };

  /**
   * Get color picker options
   * @returns
   */
  getColorPickers = (prefix: string) => {
    const { i18n, formValues } = this.props;

    return [
      {
        colorValue: _.get(formValues, `${prefix}.color`),
        fieldName: `${prefix}.color`,
        fieldLabel: i18n.translate(
          'AdminHTML.ThemeSetting.Form.collectionHeaderProductCount_Color_Item1'
        )
      }
    ];
  };

  /**
   * Get font family
   * @param {*} prefix
   * @returns
   */
  getFonts = prefix => {
    const { i18n, formValues, updateField } = this.props;
    const fontSize = _.get(formValues, `${prefix}.fontSize`, DEFAULT_FONT_SIZE);

    return (
      <div className="form-section" style={{ marginTop: '8px' }}>
        <p className="title">{i18n.translate('AdminHTML.ThemeSetting.Form.font_Title')}</p>
        <div className="form-section__item">
          <Field
            name={`${prefix}.fontSize`}
            component={RenderTextField}
            label={i18n.translate('AdminHTML.ThemeSetting.Form.fontSize_Title')}
            onBlur={(event, value) => this.onBlurTextField(event, value, `${prefix}.fontSize`)}
            normalize={(value, prevValue) =>
              normalizeNumberRangeSlider(value, prevValue, MAX_FONT_SIZE)
            }
            validate={[validateMinFontSize]}
            anotherProps={{
              connectedLeft: (
                <RangeSlider
                  value={fontSize}
                  onChange={value => updateField(`${prefix}.fontSize`, value)}
                  max={MAX_FONT_SIZE}
                  min={MIN_FONT_SIZE}
                />
              ),
              connectedRight: (
                <div className="text-field-unit">
                  <span>px</span>
                </div>
              )
            }}
          />
        </div>
      </div>
    );
  };

  /**
   * Get text description option with translation
   * @returns
   */
  getTextDescriptionOptions = () => {
    const { i18n } = this.props;

    return TEXT_DESCRIPTION_OPTIONS.map(option => {
      const { label, value } = option;
      return {
        label: i18n.translate(label, {
          t1: '{{count}}',
          t2: '{{from}}',
          t3: '{{to}}',
          t4: '{{total}}'
        }),
        value
      };
    });
  };

  render() {
    const { i18n, prefix, updateField } = this.props;

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeaderProductCount_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['CollectionProductCountThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          <div className="form-section__item">
            <Field
              name={`${prefix}.textDescription`}
              component={RenderSelect2}
              options={this.getTextDescriptionOptions()}
              label={i18n.translate(
                'AdminHTML.ThemeSetting.Form.collectionHeaderProductCount_TextDescription_Label'
              )}
              components={{ DropdownIndicator }}
              styles={styles}
              // props={{
              //   textHelper: i18n.translate(
              //     'AdminHTML.ThemeSetting.Form.collectionHeaderProductCount_TextDescription_HelpText',
              //     { t1: '{{count}}' }
              //   )
              // }}
            />
          </div>
        </div>
        <div className="form-section">
          <p className="form-section__item form-section__item--medium-spacing title">
            {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
          </p>
          <div className="form-section__item">
            {this.getColorPickers(prefix).map((item, index) => (
              <RenderColorPickerItem
                key={index}
                colorValue={item.colorValue}
                fieldName={item.fieldName}
                fieldLabel={item.fieldLabel}
                updateField={updateField}
              />
            ))}
          </div>
        </div>
        {this.getFonts(prefix)}
      </FormLayout>
    );
  }
}

export default withI18n()(CollectionHeaderProductCountForm);
